import React, { useEffect, useState } from "react";
import { Formik, Form, FormikHelpers } from "formik";
import "./EasyApplyForm.css";
import LogoIcon from "../../assets/images/LogoNameBlack.png";
import PersonalInfo, { PersonalInfoData } from "./PersonalInformation";
import Experience, { ExperienceEntry } from "./Experience";
import Education, { EducationEntry } from "./Education";
import { mainFormValidationSchema } from "./EasyApplyValidationSchema";
import { useParams, Link } from "react-router-dom";
import apiList from "../../api/apiList";
import "../../assets/fonts/font.css";
import { postRequest, multiPartRequest } from "../../api/apiServices";

interface FormData {
  personalInfo: PersonalInfoData;
  linkedin: string;
  facebook: string;
  twitter: string;
  website: string;
  message: string;
  dataProcessingConsent: boolean;
  experience: ExperienceEntry[];
  education: EducationEntry[];
}

interface APIResponse {
  applicationToken: string;
  candidateInfo: {
    firstName: string;
    lastName: string;
    email: string;
    countryCode: string;
    phone: string;
    residence: string;
    dob: string;
    linkedin: string;
    facebook: string;
    website: string | null;
    twitter: string;
    experience: Array<{
      company: string;
      role: string;
      years: number;
    }>;
    education: Array<{
      school: string;
      degree: string;
      year: number;
    }>;
    messageToHiring: string;
  };
}

const JobApplicationForm: React.FC = () => {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showSuccess, setShowSuccess] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [showErrors, setShowErrors] = useState<boolean>(false);
  const { token } = useParams();

  const [companyLogo, setCompanyLogo] = useState<string | null>(null);
  const [jobMeta, setJobMeta] = useState({
    title: "",
    location: "",
    commitment: "",
    salaryRange: "",
    highlight: "",
  });

  const initialValues: FormData = {
    personalInfo: {
      firstName: "",
      lastName: "",
      email: "",
      confirmEmail: "",
      city: "",
      phoneNumber: "",
      countryCode: "",
      dob: "",
      profileImage: null,
      cvFile: null,
    },
    linkedin: "",
    facebook: "",
    twitter: "",
    website: "",
    message: "",
    dataProcessingConsent: false,
    experience: [],
    education: [],
  };

  const handleSubmit = async (
    values: FormData,
    { setSubmitting, setStatus }: FormikHelpers<FormData>
  ) => {
    setShowErrors(true);

    try {
      setIsSubmitting(true);
      setError("");
      setStatus(null);

      const payload = {
        applicationToken: token,
        candidateInfo: {
          firstName: values.personalInfo.firstName,
          lastName: values.personalInfo.lastName,
          email: values.personalInfo.email,
          countryCode: values.personalInfo.countryCode,
          phone: values.personalInfo.phoneNumber,
          residence: values.personalInfo.city,
          dob:
            values.personalInfo.dob && values.personalInfo.dob.trim() !== ""
              ? values.personalInfo.dob
              : null,
          linkedin: values.linkedin,
          facebook: values.facebook,
          website: values.website || null,
          twitter: values.twitter,
          experience: values.experience.map((exp) => ({
            company: exp.company,
            role: exp.role || "",
            years: exp.years || 0,
          })),
          education: values.education.map((edu) => ({
            school: edu.institution || "",
            degree: edu.degree,
            year: edu.year || new Date().getFullYear(),
          })),
          messageToHiring: values.message,
        },
        coverLetter: values.personalInfo.profileImage,
        cv: values.personalInfo.cvFile,
      };

      const response = await multiPartRequest(apiList.addCandidate, payload);

      if (response?.data?.success === true) {
        setShowSuccess(true);
      }
    } catch (error: any) {
      setError(error);
      setStatus({ type: "error", message: error });
    } finally {
      setIsSubmitting(false);
      setSubmitting(false);
    }
  };

  const fetchJobData = async () => {
    try {
      const res = await postRequest(apiList.jobData, { jobToken: token });
      const jobData = res?.data?.data ?? {};
      const logoUrl = jobData?.logo ?? null;

      if (logoUrl && typeof logoUrl === "string") {
        setCompanyLogo(logoUrl);
      }

      setJobMeta({
        title: jobData?.jobTitle || jobData?.title || "",
        location: jobData?.location || jobData?.city || jobData?.country || "",
        commitment:
          jobData?.jobType || jobData?.employmentType || jobData?.type || "",
        salaryRange: jobData?.salaryRange || jobData?.salary || "",
        highlight:
          jobData?.tagline ||
          jobData?.summary ||
          "Fast-track your application in a single, elegant flow.",
      });
    } catch (error) {
      setError("Failed to fetch job data" + error);
    }
  };

  useEffect(() => {
    fetchJobData();
  }, [token]);

  if (showSuccess) {
    return (
      <div className="easy-apply-page job-apply">
        <div className="easy-apply-backdrop easy-apply-backdrop--one" />
        <div className="easy-apply-backdrop easy-apply-backdrop--two" />
        <div className="easy-apply-success card-glow">
          <div className="success-icon">
            <span className="success-ring" />
            <svg width="70" height="70" viewBox="0 0 16 16">
              <path
                d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"
                fill="#0fd1c6"
              />
            </svg>
          </div>
          <h1>Application submitted!</h1>
          <p>
            Thanks for applying. We've forwarded your details to the hiring
            squad. Keep an eye on your inbox for next steps.
          </p>
        </div>
      </div>
    );
  }

  const heroTitle = jobMeta.title || "Complete your application";
  const heroHighlight =
    jobMeta.highlight ||
    "Share your profile, upload your resume, and tell us why you're excited.";

  return (
    <div className="easy-apply-page job-apply">
      <div className="easy-apply-backdrop easy-apply-backdrop--one" />
      <div className="easy-apply-backdrop easy-apply-backdrop--two" />
      <div className="easy-apply-backdrop easy-apply-backdrop--three" />

      <div className="container easy-apply-shell">
        <header className="easy-apply-hero card-glow">
          <div className="hero-logo-badge">
            <div className="hero-logo">
              <img src={companyLogo || LogoIcon} alt="Company logo" />
            </div>
            <small>Easy Apply</small>
          </div>
          <div className="hero-copy">
            <p className="hero-eyebrow">1-step smart application</p>
            <h1>{heroTitle}</h1>
            <p className="hero-subtext">{heroHighlight}</p>
            <div className="hero-meta-grid">
              {jobMeta.location && (
                <span className="hero-meta-chip">
                  <span aria-hidden="true">📍</span> {jobMeta.location}
                </span>
              )}
              {jobMeta.commitment && (
                <span className="hero-meta-chip">
                  <span aria-hidden="true">🕒</span> {jobMeta.commitment}
                </span>
              )}
              {jobMeta.salaryRange && (
                <span className="hero-meta-chip">
                  <span aria-hidden="true">💼</span> {jobMeta.salaryRange}
                </span>
              )}
            </div>
            <div className="hero-progress">
              <span>Application progress</span>
              <div className="progress-bar">
                <span style={{ width: "94%" }} />
              </div>
            </div>
          </div>
        </header>

        <div className="easy-apply-content">
          <div className="easy-apply-form card-glow">
            <Formik
              initialValues={initialValues}
              validationSchema={mainFormValidationSchema}
              onSubmit={handleSubmit}
            >
              {({ values, errors, touched, handleChange, handleBlur }) => {
                return (
                  <Form
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        // Prevent form submit via Enter key; allow only button click to submit
                        e.preventDefault();
                      }
                    }}
                  >
                    <div className="form-container">
                      {/* Show error message if exists */}
                      {error && (
                        <div className="alert alert-danger mb-3" role="alert">
                          {error}
                        </div>
                      )}

                      <div>
                        {/* Personal Information Component */}
                        <PersonalInfo
                          value={values.personalInfo}
                          errors={errors.personalInfo}
                          onChange={handleChange}
                          touched={
                            showErrors
                              ? {
                                  firstName: true,
                                  lastName: true,
                                  email: true,
                                  confirmEmail: true,
                                  phoneNumber: true,
                                }
                              : touched.personalInfo
                          }
                          onBlur={handleBlur}
                        />

                        {/* Experience Component */}
                        <Experience experiences={values.experience} />

                        {/* Education Component */}
                        <Education education={values.education} />

                        {/* Your Profiles Section */}
                        <div className="form-section">
                          <h4 className="section-title">Your Profiles</h4>
                          <p className="section-helper">
                            Fields marked with{" "}
                            <span className="required-field">*</span> are
                            required.
                          </p>

                          <div className="profile-section">
                            <div className="profile-field">
                              <label className="form-label">LinkedIn URL</label>
                              <input
                                type="url"
                                className={`form-control ${
                                  errors.linkedin && touched.linkedin
                                    ? "is-invalid"
                                    : ""
                                }`}
                                name="linkedin"
                                value={values.linkedin}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder="Enter LinkedIn Profile URL"
                              />
                              {errors.linkedin && touched.linkedin && (
                                <div className="error-message mt-2">
                                  {errors.linkedin}
                                </div>
                              )}
                            </div>

                            <div className="profile-field">
                              <label className="form-label">Facebook URL</label>
                              <input
                                type="url"
                                className={`form-control ${
                                  errors.facebook && touched.facebook
                                    ? "is-invalid"
                                    : ""
                                }`}
                                name="facebook"
                                value={values.facebook}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder="Enter Facebook Profile URL"
                              />
                              {errors.facebook && touched.facebook && (
                                <div className="error-message mt-2">
                                  {errors.facebook}
                                </div>
                              )}
                            </div>

                            <div className="profile-field">
                              <label className="form-label">
                                X (Twitter) URL
                              </label>
                              <input
                                type="url"
                                className={`form-control ${
                                  errors.twitter && touched.twitter
                                    ? "is-invalid"
                                    : ""
                                }`}
                                name="twitter"
                                value={values.twitter}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder="Enter Twitter Profile URL"
                              />
                              {errors.twitter && touched.twitter && (
                                <div className="error-message mt-2">
                                  {errors.twitter}
                                </div>
                              )}
                            </div>

                            <div className="profile-field">
                              <label className="form-label">Website URL</label>
                              <input
                                type="url"
                                className={`form-control ${
                                  errors.website && touched.website
                                    ? "is-invalid"
                                    : ""
                                }`}
                                name="website"
                                value={values.website}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder="Enter Your Website URL"
                              />
                              {errors.website && touched.website && (
                                <div className="error-message mt-2">
                                  {errors.website}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Message Section */}
                        <div className="form-section">
                          <h4 className="section-title">
                            Message to the Hiring Team
                          </h4>
                          <p className="section-helper">
                            Let the company know why you're excited to join.
                          </p>

                          <textarea
                            className={`form-control ${
                              errors.message && touched.message
                                ? "is-invalid"
                                : ""
                            }`}
                            name="message"
                            value={values.message}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            rows={6}
                            style={{ resize: "vertical" }}
                            placeholder="Write a short note to the hiring manager..."
                          />
                          {errors.message && touched.message && (
                            <div className="error-message mt-2">
                              {errors.message}
                            </div>
                          )}
                        </div>

                        {/* Consent Section */}
                        <div className="form-section">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              name="dataProcessingConsent"
                              checked={values.dataProcessingConsent}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              id="consent"
                            />
                            <label
                              className="form-check-label consent-text"
                              htmlFor="consent"
                            >
                              Please be informed that your application to this
                              job offer will trigger some processing of your
                              personal data by the recruiting company, the data
                              controller, SmartRecruiters, the data processor,
                              has no control over such personal data processing.
                              For more information on these personal data
                              processing, please refer to{" "}
                              <Link to="/privacy-policy" className="text-link">
                                the recruiting company's privacy policy
                              </Link>
                              .
                            </label>
                          </div>
                          {errors.dataProcessingConsent &&
                            touched.dataProcessingConsent && (
                              <div className="error-message mt-2">
                                {errors.dataProcessingConsent}
                              </div>
                            )}
                        </div>

                        {/* Submit Button */}
                        <div className="form-section">
                          <div className="submit-container">
                            <button
                              type="submit"
                              className="submit-btn mt-3"
                              disabled={isSubmitting}
                            >
                              {isSubmitting ? (
                                <>
                                  <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                  ></span>
                                  Submitting...
                                </>
                              ) : (
                                "Submit application"
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Form>
                );
              }}
            </Formik>
          </div>

          <aside className="easy-apply-sidebar">
            <div className="sidebar-card card-glow">
              <p className="sidebar-eyebrow">What to expect</p>
              <h5>3-minute smart apply</h5>
              <ul className="sidebar-list">
                <li>
                  <span className="pulse-dot" />
                  Upload your profile assets
                </li>
                <li>
                  <span className="pulse-dot" />
                  Share your story & experience
                </li>
                <li>
                  <span className="pulse-dot" />
                  Hit submit and we'll do the rest
                </li>
              </ul>
            </div>

            <div className="sidebar-card gradient-card card-glow">
              <h5>Need a hand?</h5>
              <p>
                Our talent team is online to answer quick questions weekdays 9am
                - 6pm CET.
              </p>
              <div className="support-actions">
                <span>support@sprooze.com</span>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default JobApplicationForm;
